import { z } from 'zod';

export const RuleSeveritySchema = z.enum(['info', 'warn', 'error']);
export type RuleSeverity = z.infer<typeof RuleSeveritySchema>;

export const RuleScopeSchema = z.enum(['changed', 'all']);
export type RuleScope = z.infer<typeof RuleScopeSchema>;

export const LevelSchema = z.enum(['basic', 'standard', 'strict']);
export type Level = z.infer<typeof LevelSchema>;

export const PresetSchema = z.enum([
  'node-api',
  'nextjs-app',
  'python-api',
  'cli-tool',
  'library',
  'monorepo',
]);
export type Preset = z.infer<typeof PresetSchema>;

export const RuleConfigSchema = z.object({
  enabled: z.boolean().default(true),
  severity: RuleSeveritySchema.optional(),
  autofix: z.boolean().optional(),
  blocking: z.boolean().optional(),
  scope: RuleScopeSchema.optional(),
  options: z.record(z.unknown()).optional(),
});
export type RuleConfig = z.infer<typeof RuleConfigSchema>;

export const VibeGuardConfigSchema = z.object({
  preset: PresetSchema.optional(),
  level: LevelSchema.default('standard'),
  rules: z.record(z.union([z.boolean(), RuleConfigSchema])).optional(),
  include: z.array(z.string()).optional(),
  exclude: z.array(z.string()).optional(),
  ci: z
    .object({
      failOn: RuleSeveritySchema.default('error'),
      reportFormat: z.enum(['console', 'json', 'sarif']).default('console'),
    })
    .optional(),
  tools: z
    .object({
      eslint: z.boolean().default(true),
      semgrep: z.boolean().default(true),
      gitleaks: z.boolean().default(true),
      osvScanner: z.boolean().default(true),
    })
    .optional(),
});
export type VibeGuardConfig = z.infer<typeof VibeGuardConfigSchema>;

export interface RuleDefinition {
  id: string;
  name: string;
  description: string;
  category: RuleCategory;
  severity: RuleSeverity;
  autofix: boolean;
  blocking: boolean;
  scope: RuleScope;
  tool: ToolType;
  docs: string;
  rationale: string;
  fix: string;
  examples?: {
    bad?: string;
    good?: string;
  };
  levels: {
    basic: boolean;
    standard: boolean;
    strict: boolean;
  };
}

export type RuleCategory =
  | 'secrets'
  | 'deps'
  | 'security'
  | 'auth'
  | 'api'
  | 'sql'
  | 'tests'
  | 'logging'
  | 'code'
  | 'config';

export type ToolType =
  | 'eslint'
  | 'semgrep'
  | 'gitleaks'
  | 'osv-scanner'
  | 'vitest'
  | 'jest'
  | 'custom'
  | 'builtin';

export interface CheckResult {
  ruleId: string;
  severity: RuleSeverity;
  message: string;
  file?: string;
  line?: number;
  column?: number;
  endLine?: number;
  endColumn?: number;
  fix?: {
    description: string;
    replacement?: string;
  };
}

export interface CheckReport {
  timestamp: string;
  duration: number;
  preset?: Preset;
  level: Level;
  summary: {
    total: number;
    errors: number;
    warnings: number;
    infos: number;
    fixed: number;
  };
  score: number;
  results: CheckResult[];
  tools: {
    name: string;
    version?: string;
    duration: number;
    results: number;
  }[];
}
